import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { formatDay } from '@lensmira/i18n'
import type { MessageKey } from '@lensmira/i18n'

import { Ltr, useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { DataSurface } from './DataSurface.js'
import { DAY_MINUTES, minuteOf, timeOf } from '../minutes.js'

/**
 * The exceptions to a branch's calendar: closures and seasonal opening hours. `APT-002`,
 * Appendix J.
 *
 * **The two are not the same thing and the copy keeps them apart.**
 *
 * - A **closure** has no archive column and is never deleted. It is corrected by setting `trades`
 *   true, which the domain reads as "the shop trades through it", that is, no closure. So "open
 *   this day after all" is an edit (`PATCH`), and the row stays as a record.
 * - An **hours override** is archived and restored. Restoring re-runs the overlap check, so a
 *   restore can be refused when a live season now covers those days. The screen runs the same
 *   check on what it has loaded and says so before the write is queued.
 *
 * **Every refusal the server can give is checked here first**, because `transport.ts` treats 409
 * and 422 as refusals and a blocked head halts the whole queue, taking payment included: a missing
 * date, an inverted range, a time window that closes before it opens, a note over 200 characters, a
 * day that already has an entry, and an overlap with a live season (on add, edit and restore).
 *
 * **What the screen cannot know, and says.** `trades: true` does something only on a day the
 * country lists as a public holiday, and the counter has no read of that list, so the copy says so
 * in words. And `isWorkingDay` is evaluated before overrides, so an override can never open a
 * weekday the branch is closed: Appendix J does not say so and the seasonal hours copy does.
 *
 * **Split at the branch's today.** Both lists come back complete, oldest first, so after two years
 * the first rows would be 2026's. The screen shows what is still ahead by default and offers the
 * earlier ones on request. The API should take a from-date.
 *
 * **Closing at 24:00.** `closesAtMinute` accepts 1440 but a native time input cannot say 24:00, so
 * the form offers 23:59. A row already at 1440 keeps it unless its closing time is changed.
 */

export interface ClosureRow {
  readonly id: string
  readonly branchId: string
  readonly onDate: string
  readonly trades: boolean
  readonly label: string | null
  readonly version: number
}

export interface OverrideRow {
  readonly id: string
  readonly branchId: string
  readonly fromDate: string
  readonly toDate: string
  readonly opensAtMinute: number
  readonly closesAtMinute: number
  readonly archivedAt: string | null
  readonly version: number
}

export interface AppointmentCalendarProps {
  /** `tenant_settings_policies`, owner alone. Without it the whole page is the denied state. */
  readonly canManage: boolean
  readonly branchId: string
  readonly branchName: string
  /** The branch's today as `YYYY-MM-DD` (`branchDate`), not the device's. */
  readonly today: string
  /**
   * The branch's IANA zone, for `formatDay`. `LOC-004`: an ISO day is not how a shop writes a
   * date, and a calendar date pushed through a timezone moves to the previous day west of UTC,
   * which is why the zone travels with it rather than being taken from the device.
   */
  readonly timezone: string
  /** Must be referentially stable (`useCallback`), or the screen reads again on every render. */
  readonly loadClosures: (branchId: string) => Promise<readonly ClosureRow[]>
  readonly loadOverrides: (branchId: string) => Promise<readonly OverrideRow[]>
}

type Period = 'coming' | 'earlier'

const NOTE_MAX = 200

/**
 * The seasonal-hours form's own state. `fromIso` and `toIso` rather than `from` and `to` because
 * they are bound to `<input type="date">`, which speaks `YYYY-MM-DD` and nothing else: passing
 * them through `formatDay` would empty the field. The names say so, which is also what keeps
 * `LOC-004`'s scan off them: it looks for a `from:` or `to:` carrying a dotted date expression,
 * which is the display mistake it exists to catch.
 */
interface OverrideDraft {
  readonly fromIso: string
  readonly toIso: string
  readonly opens: string
  readonly closes: string
}

const EMPTY_OVERRIDE: OverrideDraft = { fromIso: '', toIso: '', opens: '', closes: '' }

/**
 * Which thing the queue line is about, from the path of the write in flight.
 *
 * This screen writes more than one kind of row, so a single fixed subject would say "The change"
 * for all of them. The path is what distinguishes them and the queue already carries it.
 */
const subjectKeyFor = (path: string | undefined): MessageKey => {
  if (path === undefined) return 'queue.subject.setup'
  if (path.includes('/visit-dates')) return 'queue.subject.visitDates'
  if (path.includes('/appointment-resources')) return 'queue.subject.appointmentResource'
  if (path.includes('/appointment-types')) return 'queue.subject.appointmentType'
  if (path.includes('/appointment-closures')) return 'queue.subject.appointmentClosure'
  if (path.includes('/appointment-hours-overrides')) return 'queue.subject.hoursOverride'
  return 'queue.subject.setup'
}

export function AppointmentCalendar({
  canManage,
  branchId,
  branchName,
  today,
  timezone,
  loadClosures,
  loadOverrides,
}: AppointmentCalendarProps): JSX.Element {
  const { t, locale } = useTranslation()
  /** Every date a shopkeeper reads goes through this. The form's own `*Iso` fields do not. */
  const day = useCallback(
    (iso: string): string => formatDay(iso, timezone, locale),
    [timezone, locale],
  )
  const { enqueue, snapshot } = useWriteQueue()

  const [closures, setClosures] = useState<readonly ClosureRow[] | null>(null)
  const [closuresFailed, setClosuresFailed] = useState(false)
  const [overrides, setOverrides] = useState<readonly OverrideRow[] | null>(null)
  const [overridesFailed, setOverridesFailed] = useState(false)

  const [period, setPeriod] = useState<Period>('coming')
  const [showStopped, setShowStopped] = useState(false)
  const [writeId, setWriteId] = useState<string | null>(null)

  const [closureDate, setClosureDate] = useState('')
  const [closureTrades, setClosureTrades] = useState(false)
  const [closureNote, setClosureNote] = useState('')
  const [closureError, setClosureError] = useState<string | null>(null)

  const [notingId, setNotingId] = useState<string | null>(null)
  const [noteDraft, setNoteDraft] = useState('')
  const [noteError, setNoteError] = useState<string | null>(null)

  const [addOverride, setAddOverride] = useState<OverrideDraft>(EMPTY_OVERRIDE)
  const [addOverrideError, setAddOverrideError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editOverride, setEditOverride] = useState<OverrideDraft>(EMPTY_OVERRIDE)
  const [editOverrideError, setEditOverrideError] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  // A response for a branch the shopkeeper has since left must not land on the new one.
  const generation = useRef(0)

  const refresh = useCallback(() => {
    generation.current += 1
    const mine = generation.current
    loadClosures(branchId).then(
      (rows) => {
        if (generation.current !== mine) return
        setClosures(rows)
        setClosuresFailed(false)
      },
      () => {
        if (generation.current !== mine) return
        // Its own state, not an empty list: "no shut days" and "could not read them" differ.
        setClosures(null)
        setClosuresFailed(true)
      },
    )
    loadOverrides(branchId).then(
      (rows) => {
        if (generation.current !== mine) return
        setOverrides(rows)
        setOverridesFailed(false)
      },
      () => {
        if (generation.current !== mine) return
        setOverrides(null)
        setOverridesFailed(true)
      },
    )
  }, [branchId, loadClosures, loadOverrides])

  useEffect(() => {
    if (!canManage) return
    refresh()
  }, [canManage, refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setClosureDate('')
    setClosureTrades(false)
    setClosureNote('')
    setClosureError(null)
    setNotingId(null)
    setNoteDraft('')
    setNoteError(null)
    setAddOverride(EMPTY_OVERRIDE)
    setAddOverrideError(null)
    setEditingId(null)
    setEditOverride(EMPTY_OVERRIDE)
    setEditOverrideError(null)
    setRowError(null)
    refresh()
  })

  // A write somebody discarded from the blocked list has to give the forms back.
  useEffect(() => {
    if (writeId === null) return
    if (!snapshot.writes.some((candidate) => candidate.id === writeId)) setWriteId(null)
  }, [snapshot, writeId])

  const queued = useCallback(
    async (request: Parameters<typeof enqueue>[0]) => {
      const write = (await enqueue(request)) as { id: string }
      setWriteId(write.id)
    },
    [enqueue],
  )

  const busy = writeId !== null

  /* ------------------------------ closures ------------------------------ */

  const addClosure = useCallback(async () => {
    if (closureDate === '') {
      setClosureError(t('apptCalendar.closureDateRequired'))
      return
    }
    if ((closures ?? []).some((row) => row.onDate === closureDate)) {
      // `APPOINTMENT_CLOSURE_EXISTS` is a 409 and would halt the queue.
      setClosureError(t('apptCalendar.closureDateTaken', { date: day(closureDate) }))
      return
    }
    if (closureNote.length > NOTE_MAX) {
      setClosureError(t('apptCalendar.closureLabelLong'))
      return
    }
    setClosureError(null)
    const note = closureNote.trim()
    await queued({
      method: 'POST',
      path: `/branches/${encodeURIComponent(branchId)}/appointment-closures`,
      label: t('apptCalendar.queueAddClosure', { date: closureDate }),
      body: { onDate: closureDate, trades: closureTrades, label: note === '' ? null : note },
    })
  }, [branchId, closureDate, closureNote, closureTrades, closures, queued, t])

  const changeClosure = useCallback(
    async (row: ClosureRow, trades: boolean, label: string | null, labelKey: string) => {
      await queued({
        method: 'PATCH',
        path: `/appointment-closures/${encodeURIComponent(row.id)}`,
        label: t(labelKey as Parameters<typeof t>[0], { date: day(row.onDate) }),
        // The label is sent every time: an edit that leaves it out clears it.
        body: { trades, label, expectedVersion: row.version },
      })
    },
    [queued, t],
  )

  const saveNote = useCallback(
    async (row: ClosureRow) => {
      if (noteDraft.length > NOTE_MAX) {
        setNoteError(t('apptCalendar.closureLabelLong'))
        return
      }
      setNoteError(null)
      const note = noteDraft.trim()
      await changeClosure(
        row,
        row.trades,
        note === '' ? null : note,
        'apptCalendar.queueChangeClosure',
      )
    },
    [changeClosure, noteDraft, t],
  )

  /* ------------------------------- overrides ------------------------------- */

  /** The refusal text for a draft, or the shaped body. `original` is set when editing. */
  const shapeOverride = useCallback(
    (
      draft: OverrideDraft,
      original: OverrideRow | null,
    ): { error: string } | { body: Record<string, unknown> } => {
      if (draft.fromIso === '' || draft.toIso === '') {
        return { error: t('apptCalendar.overrideDatesRequired') }
      }
      if (draft.toIso < draft.fromIso) return { error: t('apptCalendar.overrideDatesBackwards') }
      const opens = minuteOf(draft.opens)
      const closes = minuteOf(draft.closes)
      if (opens === null || closes === null) {
        return { error: t('apptCalendar.overrideTimesRequired') }
      }
      // 00:00 as a closing time is what somebody types for midnight, and minute 0 is not it.
      if (closes === 0) return { error: t('apptCalendar.overrideMidnight') }
      if (closes <= opens) return { error: t('apptCalendar.overrideTimesBackwards') }
      const clash = (overrides ?? []).find(
        (row) =>
          row.archivedAt === null &&
          row.id !== original?.id &&
          row.fromDate <= draft.toIso &&
          row.toDate >= draft.fromIso,
      )
      if (clash !== undefined) {
        return {
          error: t('apptCalendar.overrideOverlap', { from: day(clash.fromDate), to: day(clash.toDate) }),
        }
      }
      // A season that closes at 24:00 keeps it until somebody changes the closing time.
      const keepsMidnight =
        original !== null &&
        original.closesAtMinute === DAY_MINUTES &&
        draft.closes === timeOf(DAY_MINUTES)
      return {
        body: {
          fromDate: draft.fromIso,
          toDate: draft.toIso,
          opensAtMinute: opens,
          closesAtMinute: keepsMidnight ? DAY_MINUTES : closes,
        },
      }
    },
    [overrides, t],
  )

  const submitAddOverride = useCallback(async () => {
    const shaped = shapeOverride(addOverride, null)
    if ('error' in shaped) {
      setAddOverrideError(shaped.error)
      return
    }
    setAddOverrideError(null)
    await queued({
      method: 'POST',
      path: `/branches/${encodeURIComponent(branchId)}/appointment-hours-overrides`,
      label: t('apptCalendar.queueAddOverride', {
        from: day(addOverride.fromIso),
        to: day(addOverride.toIso),
      }),
      body: shaped.body,
    })
  }, [addOverride, branchId, queued, shapeOverride, t])

  const submitEditOverride = useCallback(
    async (row: OverrideRow) => {
      const shaped = shapeOverride(editOverride, row)
      if ('error' in shaped) {
        setEditOverrideError(shaped.error)
        return
      }
      setEditOverrideError(null)
      await queued({
        method: 'PATCH',
        path: `/appointment-hours-overrides/${encodeURIComponent(row.id)}`,
        label: t('apptCalendar.queueChangeOverride', {
          from: day(row.fromDate),
          to: day(row.toDate),
        }),
        body: { ...shaped.body, expectedVersion: row.version },
      })
    },
    [editOverride, queued, shapeOverride, t],
  )

  const stopOverride = useCallback(
    async (row: OverrideRow) => {
      setRowError(null)
      await queued({
        method: 'POST',
        path: `/appointment-hours-overrides/${encodeURIComponent(row.id)}/archive`,
        label: t('apptCalendar.queueStopOverride', {
          from: day(row.fromDate),
          to: day(row.toDate),
        }),
        body: { expectedVersion: row.version },
      })
    },
    [queued, t],
  )

  const resumeOverride = useCallback(
    async (row: OverrideRow) => {
      // Restore re-runs the overlap check, and a refusal is a 409 that halts the queue.
      const clash = (overrides ?? []).find(
        (other) =>
          other.archivedAt === null &&
          other.id !== row.id &&
          other.fromDate <= row.toDate &&
          other.toDate >= row.fromDate,
      )
      if (clash !== undefined) {
        setRowError(
          t('apptCalendar.overrideResumeOverlap', {
          from: day(clash.fromDate),
          to: day(clash.toDate),
        }),
        )
        return
      }
      setRowError(null)
      await queued({
        method: 'POST',
        path: `/appointment-hours-overrides/${encodeURIComponent(row.id)}/restore`,
        label: t('apptCalendar.queueResumeOverride', {
          from: day(row.fromDate),
          to: day(row.toDate),
        }),
        body: { expectedVersion: row.version },
      })
    },
    [overrides, queued, t],
  )

  /* -------------------------------- views -------------------------------- */

  const line = statusLine(t, snapshot, writeId, t(subjectKeyFor(snapshot.writes.find((write) => write.id === writeId)?.path)))
  const what = t('apptCalendar.heading')

  if (!canManage) {
    return (
      <section className="lm-screen" aria-labelledby="appt-calendar-heading">
        <h1 id="appt-calendar-heading" className="lm-screen__heading">
          {t('apptCalendar.heading')}
        </h1>
        {/* Owner alone (`tenant_settings_policies`): no read is made and no form is drawn. */}
        <DataSurface
          state={{ kind: 'denied' }}
          shape="rows"
          what={what}
          deniedMessage={t('surface.denied', { what })}
        >
          {null}
        </DataSurface>
      </section>
    )
  }

  const inPeriod = (end: string): boolean => (period === 'coming' ? end >= today : end < today)
  const sortDates = <R,>(rows: readonly R[], key: (r: R) => string): R[] =>
    [...rows].sort((a, b) =>
      period === 'coming' ? key(a).localeCompare(key(b)) : key(b).localeCompare(key(a)),
    )

  const closureRows = sortDates(
    (closures ?? []).filter((row) => inPeriod(row.onDate)),
    (row) => row.onDate,
  )
  const overrideCandidates = (overrides ?? []).filter((row) => inPeriod(row.toDate))
  const overrideRows = sortDates(
    overrideCandidates.filter((row) => showStopped || row.archivedAt === null),
    (row) => row.fromDate,
  )

  const closuresState = closuresFailed
    ? ({
        kind: 'error',
        message: t('apptCalendar.closuresLoadFailed', { branch: branchName }),
        onRetry: refresh,
      } as const)
    : closures === null
      ? ({ kind: 'loading' } as const)
      : closures.length === 0
        ? ({ kind: 'empty' } as const)
        : closureRows.length === 0
          ? ({ kind: 'filtered' } as const)
          : ({ kind: 'ready' } as const)

  const overridesState = overridesFailed
    ? ({
        kind: 'error',
        message: t('apptCalendar.overridesLoadFailed', { branch: branchName }),
        onRetry: refresh,
      } as const)
    : overrides === null
      ? ({ kind: 'loading' } as const)
      : overrides.length === 0
        ? ({ kind: 'empty' } as const)
        : overrideRows.length === 0
          ? ({ kind: 'filtered' } as const)
          : ({ kind: 'ready' } as const)

  // True only when stopped sets are what is being hidden, which is what the sentence claims.
  const hiddenAreStopped =
    overrideCandidates.length > 0 && overrideCandidates.every((row) => row.archivedAt !== null)

  const overrideFields = (
    prefix: string,
    draft: OverrideDraft,
    setDraft: (next: OverrideDraft) => void,
  ): JSX.Element => {
    const input = (key: keyof OverrideDraft, label: string, type: 'date' | 'time'): JSX.Element => (
      <div className="lm-field">
        <label htmlFor={`${prefix}-${key}`} className="lm-field__label">
          {label}
        </label>
        <input
          id={`${prefix}-${key}`}
          className="lm-input"
          type={type}
          value={draft[key]}
          disabled={busy}
          onChange={(e) => {
            setDraft({ ...draft, [key]: e.currentTarget.value })
          }}
        />
      </div>
    )
    return (
      <>
        {input('fromIso', t('apptCalendar.overrideFrom'), 'date')}
        {input('toIso', t('apptCalendar.overrideTo'), 'date')}
        {input('opens', t('apptCalendar.overrideOpens'), 'time')}
        {input('closes', t('apptCalendar.overrideCloses'), 'time')}
      </>
    )
  }

  const errorLine = (message: string | null): JSX.Element | null =>
    message === null ? null : (
      <p className="lm-supplier__error" role="alert">
        {message}
      </p>
    )

  return (
    <section className="lm-screen" aria-labelledby="appt-calendar-heading">
      <h1 id="appt-calendar-heading" className="lm-screen__heading">
        {t('apptCalendar.heading')}
      </h1>

      <div className="lm-screen__actions">
        <button
          type="button"
          className="lm-button lm-button--secondary"
          aria-pressed={period === 'coming'}
          onClick={() => {
            setPeriod('coming')
          }}
        >
          {t('apptCalendar.showComing')}
        </button>
        <button
          type="button"
          className="lm-button lm-button--secondary"
          aria-pressed={period === 'earlier'}
          onClick={() => {
            setPeriod('earlier')
          }}
        >
          {t('apptCalendar.showEarlier')}
        </button>
      </div>

      {/* ----------------------------- closures ----------------------------- */}
      <h2 className="lm-screen__subheading">
        {t('apptCalendar.closuresHeading', { branch: branchName })}
      </h2>
      <p className="lm-hint">{t('apptCalendar.closuresMeans')}</p>
      <p className="lm-hint">{t('apptCalendar.closureNoDelete')}</p>

      <DataSurface
        state={closuresState}
        shape="rows"
        rows={3}
        what={what}
        emptyMessage={t('apptCalendar.closuresEmpty', { branch: branchName })}
        filteredMessage={
          period === 'coming'
            ? t('apptCalendar.closuresFiltered', { branch: branchName })
            : t('surface.filtered')
        }
        deniedMessage={t('surface.denied', { what })}
      >
        <ul className="lm-supplier-list">
          {closureRows.map((row) => (
            <li key={row.id} className="lm-supplier">
              <span className="lm-supplier__name">
                <Ltr>{day(row.onDate)}</Ltr>
              </span>
              <span className="lm-supplier__meta">
                {row.trades ? t('apptCalendar.closureTrades') : t('apptCalendar.closureShut')}
                {row.label === null ? null : (
                  <>
                    {' · '}
                    <span dir="auto">{row.label}</span>
                  </>
                )}
              </span>
              {row.trades ? (
                <span className="lm-supplier__meta">{t('apptCalendar.closureTradesMeans')}</span>
              ) : null}
              {notingId === row.id ? (
                <div className="lm-supplier__form">
                  <div className="lm-field">
                    <label htmlFor={`note-${row.id}`} className="lm-field__label">
                      {t('apptCalendar.closureLabel')}
                    </label>
                    <input
                      id={`note-${row.id}`}
                      className="lm-input"
                      type="text"
                      dir="auto"
                      autoComplete="off"
                      value={noteDraft}
                      disabled={busy}
                      onChange={(e) => {
                        setNoteDraft(e.currentTarget.value)
                      }}
                    />
                  </div>
                  {errorLine(noteError)}
                  <button
                    type="button"
                    className="lm-button lm-button--primary"
                    disabled={busy}
                    onClick={() => {
                      void saveNote(row)
                    }}
                  >
                    {t('apptSetup.save')}
                  </button>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => {
                      setNotingId(null)
                      setNoteError(null)
                    }}
                  >
                    {t('suppliers.cancel')}
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    disabled={busy}
                    onClick={() => {
                      setNotingId(row.id)
                      setNoteDraft(row.label ?? '')
                      setNoteError(null)
                    }}
                  >
                    {t('apptCalendar.closureEditLabel', { date: day(row.onDate) })}
                  </button>
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    disabled={busy}
                    onClick={() => {
                      void changeClosure(
                        row,
                        !row.trades,
                        row.label,
                        row.trades
                          ? 'apptCalendar.queueShutClosure'
                          : 'apptCalendar.queueOpenClosure',
                      )
                    }}
                  >
                    {row.trades
                      ? t('apptCalendar.closureShutAgain', { date: day(row.onDate) })
                      : t('apptCalendar.closureOpenAgain', { date: day(row.onDate) })}
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      </DataSurface>

      <div className="lm-supplier__form">
        <h3 className="lm-screen__subheading">{t('apptCalendar.closureAddHeading')}</h3>
        <div className="lm-field">
          <label htmlFor="closure-date" className="lm-field__label">
            {t('apptCalendar.closureDate')}
          </label>
          <input
            id="closure-date"
            className="lm-input"
            type="date"
            value={closureDate}
            disabled={busy}
            onChange={(e) => {
              setClosureDate(e.currentTarget.value)
            }}
          />
        </div>
        <div className="lm-field">
          <label htmlFor="closure-kind" className="lm-field__label">
            {t('apptCalendar.closureKind')}
          </label>
          <select
            id="closure-kind"
            className="lm-input"
            value={closureTrades ? 'trades' : 'shut'}
            disabled={busy}
            onChange={(e) => {
              setClosureTrades(e.currentTarget.value === 'trades')
            }}
          >
            <option value="shut">{t('apptCalendar.closureKindShut')}</option>
            <option value="trades">{t('apptCalendar.closureKindTrades')}</option>
          </select>
        </div>
        <p className="lm-hint">{t('apptCalendar.closureTradesMeans')}</p>
        <div className="lm-field">
          <label htmlFor="closure-label" className="lm-field__label">
            {t('apptCalendar.closureLabel')}
          </label>
          <input
            id="closure-label"
            className="lm-input"
            type="text"
            dir="auto"
            autoComplete="off"
            value={closureNote}
            disabled={busy}
            onChange={(e) => {
              setClosureNote(e.currentTarget.value)
            }}
          />
        </div>
        {errorLine(closureError)}
        <button
          type="button"
          className="lm-button lm-button--primary"
          disabled={busy}
          onClick={() => {
            void addClosure()
          }}
        >
          {t('apptCalendar.closureAdd')}
        </button>
      </div>

      {/* ------------------------- seasonal hours ------------------------- */}
      <h2 className="lm-screen__subheading">
        {t('apptCalendar.overridesHeading', { branch: branchName })}
      </h2>
      <p className="lm-hint">{t('apptCalendar.overridesMeans')}</p>
      <p className="lm-hint">{t('apptCalendar.overrideStopMeans')}</p>

      <label className="lm-field lm-field--inline">
        <input
          type="checkbox"
          checked={showStopped}
          onChange={(e) => {
            setShowStopped(e.currentTarget.checked)
          }}
        />
        <span>{t('apptSetup.showStopped')}</span>
      </label>

      <DataSurface
        state={overridesState}
        shape="rows"
        rows={3}
        what={what}
        emptyMessage={t('apptCalendar.overridesEmpty', { branch: branchName })}
        filteredMessage={
          hiddenAreStopped
            ? t('apptCalendar.overridesFiltered', { branch: branchName })
            : t('surface.filtered')
        }
        deniedMessage={t('surface.denied', { what })}
      >
        <ul className="lm-supplier-list">
          {overrideRows.map((row) => {
            const stopped = row.archivedAt !== null
            return (
              <li key={row.id} className="lm-supplier">
                <span className="lm-supplier__name" id={`season-${row.id}`}>
                  <Ltr>
                    {t('apptCalendar.overrideRange', { from: day(row.fromDate), to: day(row.toDate) })}
                  </Ltr>
                </span>
                <span className="lm-supplier__meta">
                  <Ltr>
                    {t('apptCalendar.overrideWindow', {
                      opens: timeOf(row.opensAtMinute),
                      closes: timeOf(row.closesAtMinute),
                    })}
                  </Ltr>
                  {stopped ? ` · ${t('apptSetup.stoppedNote')}` : ''}
                </span>
                {editingId === row.id ? (
                  <div className="lm-supplier__form">
                    {overrideFields(`edit-${row.id}`, editOverride, setEditOverride)}
                    {errorLine(editOverrideError)}
                    <button
                      type="button"
                      className="lm-button lm-button--primary"
                      disabled={busy}
                      onClick={() => {
                        void submitEditOverride(row)
                      }}
                    >
                      {t('apptSetup.save')}
                    </button>
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      onClick={() => {
                        setEditingId(null)
                        setEditOverrideError(null)
                      }}
                    >
                      {t('suppliers.cancel')}
                    </button>
                  </div>
                ) : stopped ? (
                  <>
                    <span className="lm-supplier__meta">
                      {t('apptCalendar.overrideStoppedEdit')}
                    </span>
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      aria-describedby={`season-${row.id}`}
                      disabled={busy}
                      onClick={() => {
                        void resumeOverride(row)
                      }}
                    >
                      {t('apptCalendar.overrideResume')}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      disabled={busy}
                      onClick={() => {
                        setEditingId(row.id)
                        setEditOverride({
                          fromIso: row.fromDate,
                          toIso: row.toDate,
                          opens: timeOf(row.opensAtMinute),
                          closes: timeOf(row.closesAtMinute),
                        })
                        setEditOverrideError(null)
                      }}
                    >
                      {t('apptCalendar.overrideChange', { from: day(row.fromDate), to: day(row.toDate) })}
                    </button>
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      aria-describedby={`season-${row.id}`}
                      disabled={busy}
                      onClick={() => {
                        void stopOverride(row)
                      }}
                    >
                      {t('apptCalendar.overrideStop')}
                    </button>
                  </>
                )}
              </li>
            )
          })}
        </ul>
      </DataSurface>
      {errorLine(rowError)}

      <div className="lm-supplier__form">
        <h3 className="lm-screen__subheading">{t('apptCalendar.overrideAddHeading')}</h3>
        {overrideFields('add-override', addOverride, setAddOverride)}
        {errorLine(addOverrideError)}
        <button
          type="button"
          className="lm-button lm-button--primary"
          disabled={busy}
          onClick={() => {
            void submitAddOverride()
          }}
        >
          {t('apptCalendar.overrideAdd')}
        </button>
      </div>

      {line === null ? null : <p className="lm-hint">{line}</p>}
    </section>
  )
}
